/**
 * 홈 「오늘 숫자 띠 + 분양 임박 현장」을 지역 선택에 맞추기 위한 시·도별 묶음(순수 · 훅·DB 없음).
 *   서버(홈 page)가 전국 임박 후보와 일정 행을 시·도로 나눠 내려주고, 브라우저(HomeTodayImminent)가 선택 지역 묶음을 고른다.
 *   시·군까지 고르면 그 시·군 행만 거른다(띠 숫자는 일정 행에 시·군이 없어 시·군 단계에서는 숨긴다).
 */
import { sidoShortName } from '@/lib/cl-core/names';
import type { ImminentLink } from '@/lib/home/imminent-links';
import { todayStripCounts, type TodayStripCounts } from '@/components/home/TodayStrip';

/** 시·도 하나에 내려 보낼 임박 행 수(시·군 거르기 여유분 포함). */
export const REGION_IMMINENT_POOL = 12;
/** 화면에 그리는 행 수. */
export const REGION_IMMINENT_SHOW = 8;

export interface RegionTodayBundle { rows: ImminentLink[]; strip: TodayStripCounts }

type Ev = Parameters<typeof todayStripCounts>[0] extends ReadonlyArray<infer T> | null | undefined ? T & { region?: string | null } : never;

export function groupTodayByRegion(
  pool: readonly ImminentLink[],
  events: ReadonlyArray<Ev> | null | undefined,
  today: string,
): Record<string, RegionTodayBundle> {
  const out: Record<string, RegionTodayBundle> = {};
  const evBy = new Map<string, Ev[]>();
  for (const e of events ?? []) {
    const k = sidoShortName(e.region ?? null);
    if (!k) continue;
    (evBy.get(k) ?? evBy.set(k, []).get(k)!).push(e);
  }
  const rowsBy = new Map<string, ImminentLink[]>();
  for (const r of pool) {
    const k = sidoShortName(r.region);
    if (!k) continue;
    const list = rowsBy.get(k) ?? rowsBy.set(k, []).get(k)!;
    if (list.length < REGION_IMMINENT_POOL) list.push(r);
  }
  for (const k of new Set([...evBy.keys(), ...rowsBy.keys()])) {
    out[k] = { rows: rowsBy.get(k) ?? [], strip: todayStripCounts(evBy.get(k) ?? [], today) };
  }
  return out;
}

/** 선택 지역 라벨(「부산」 · 「경남 김해시」) → 시·도 키 + 시·군 이름. */
export function parseRegionLabel(label: string | null | undefined): { sido: string | null; sigungu: string | null } {
  const parts = String(label ?? '').trim().split(/\s+/).filter(Boolean);
  return { sido: sidoShortName(parts[0] ?? null), sigungu: parts.length > 1 ? parts.slice(1).join(' ') : null };
}

/** 시·군 이름 비교 — 「김해시」 ↔ 「김해」 · 「수원시 영통구」 ↔ 「수원시」 를 같은 곳으로 본다. */
const stem = (s: string) => s.replace(/\s+/g, '').replace(/(특례시|시|군|구)$/u, '');
export function sameSigungu(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const x = stem(a), y = stem(b);
  return !!x && !!y && (x === y || a.replace(/\s+/g, '').startsWith(b.replace(/\s+/g, '')) || b.replace(/\s+/g, '').startsWith(a.replace(/\s+/g, '')));
}

/** 선택 지역의 행·띠. 시·군 단계면 행만 거르고 띠는 null(숨김). 묶음이 없으면 빈 행. */
export function pickRegionToday(
  byRegion: Record<string, RegionTodayBundle>,
  label: string | null | undefined,
): { sido: string | null; rows: ImminentLink[]; strip: TodayStripCounts | null } {
  const { sido, sigungu } = parseRegionLabel(label);
  if (!sido) return { sido: null, rows: [], strip: null };
  const b = byRegion[sido];
  if (!b) return { sido, rows: [], strip: sigungu ? null : { closingToday: 0, open: 0, weekNotice: 0, weekWinner: 0 } };
  const rows = (sigungu ? b.rows.filter((r) => sameSigungu(r.sigungu, sigungu)) : b.rows).slice(0, REGION_IMMINENT_SHOW);
  return { sido, rows, strip: sigungu ? null : b.strip };
}
